import type { AvatarColorScheme } from 'src/components/ui/avatar';
import type { ComponentType, SVGProps } from 'react';
import { ReactComponent as ConfluenceIcon } from 'src/assets/tailwind/confluence.svg';
import { ReactComponent as GoogleDriveIcon } from 'src/assets/tailwind/google_drive.svg';
import { ReactComponent as OneDriveIcon } from 'src/assets/tailwind/one-drive.svg';
import { ReactComponent as SharePointIcon } from 'src/assets/tailwind/share-point.svg';
import { RESOURCE_PROVIDERS_CODE } from 'src/content/DataLibrary/enum';
import { RESOURCE_PROVIDERS } from 'src/types/enum';

type ProviderIcon = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * Connector badge icons, keyed by the provider's *type* string (`'Sharepoint'`
 * etc. from `RESOURCE_PROVIDERS`). Shared by `ResourceAvatar` and Advanced
 * Search's connector panel, which previously kept identical private copies.
 */
export const PROVIDER_TYPE_ICONS: Record<string, ProviderIcon> = {
  [RESOURCE_PROVIDERS.SHAREPOINT]: SharePointIcon,
  [RESOURCE_PROVIDERS.ONEDRIVE]: OneDriveIcon,
  [RESOURCE_PROVIDERS.CONFLUENCE]: ConfluenceIcon,
  [RESOURCE_PROVIDERS.GOOGLEDRIVE]: GoogleDriveIcon
};

/**
 * The same icons keyed by machine *code* (`RESOURCE_PROVIDERS_CODE`, e.g.
 * `E_RP_SP_ALP_EA`) rather than type string — kept separate from
 * `PROVIDER_TYPE_ICONS` because the two key spaces are not interchangeable.
 */
export const PROVIDER_CODE_ICONS: Record<string, ProviderIcon> = {
  [RESOURCE_PROVIDERS_CODE.SHAREPOINT]: SharePointIcon,
  [RESOURCE_PROVIDERS_CODE.ONEDRIVE]: OneDriveIcon,
  [RESOURCE_PROVIDERS_CODE.CONFLUENCE]: ConfluenceIcon,
  [RESOURCE_PROVIDERS_CODE.GOOGLEDRIVE]: GoogleDriveIcon
};

/**
 * Strips the trailing `_<id>` suffix a provider code carries per connected
 * instance, yielding the bare code used as a `PROVIDER_CODE_ICONS` key.
 */
export const getProviderCode = (val: string) => {
  const lastIndex = val.lastIndexOf('_');
  return lastIndex > -1 ? val.slice(0, lastIndex) : val;
};

export const SCHEME_TO_CSS_VAR: Record<string, string> = {
  brand: 'var(--Color-Primary-500)',
  blue_cyan: 'var(--Color-Info-500)',
  caramel: 'var(--Color-Warning-500)',
  red: 'var(--Color-Error-500)',
  purple: 'var(--Others-Purple-500)',
  green: 'var(--Color-Success-500)',
  yellow: 'var(--Others-Yellow-500)',
  lime: 'var(--Others-Lime-500)',
  teal: 'var(--Others-Teal-500)',
  pink: 'var(--Others-Pink-500)'
};

/** The seedable scheme names: the `Avatar` union minus the two with no colour. */
export type ColorSchemeName = Exclude<AvatarColorScheme, 'default' | 'none'>;

/* Derived from the map so order and membership can't drift from it. */
const COLOR_SCHEME_NAMES = Object.keys(SCHEME_TO_CSS_VAR) as ColorSchemeName[];

export const getRandomColorScheme = (): ColorSchemeName => {
  return COLOR_SCHEME_NAMES[
    Math.floor(Math.random() * COLOR_SCHEME_NAMES.length)
  ];
};

export const getColorSchemeBySeed = (
  seed: string | number
): ColorSchemeName => {
  const numeric = Number(seed);
  const index = Number.isFinite(numeric)
    ? Math.abs(numeric)
    : String(seed).length;
  return COLOR_SCHEME_NAMES[index % COLOR_SCHEME_NAMES.length];
};

// TODO: Best way is to migrate existing colors of workspaces in backend
export const COLOR_SCHEME_MAP = {
  'rgb(0, 12, 87)': 'blue_cyan',
  'rgb(251, 39, 93)': 'red',
  'rgb(0, 202, 114)': 'green',
  'rgb(163, 88, 208)': 'purple',
  'rgb(89, 90, 212)': 'purple',
  'rgb(28, 31, 59)': 'brand',
  'rgb(102, 204, 255)': 'blue_cyan',
  'rgb(253, 171, 61)': 'caramel',
  'rgb(255, 203, 0)': 'yellow',
  'rgb(0, 154, 255)': 'blue_cyan',
  'rgb(246, 95, 124)': 'pink',
  'rgb(255, 21, 138)': 'pink',
  'rgb(226, 68, 92)': 'red',
  'rgb(128, 128, 128)': 'brand',
  'rgb(87, 155, 252)': 'blue_cyan',
  'rgb(187, 51, 84)': 'red',
  'rgb(3, 127, 76)': 'green',
  'rgb(255, 90, 196)': 'pink',
  'rgb(31, 118, 194)': 'blue_cyan',
  'rgb(255, 100, 46)': 'caramel',
  'rgb(156, 211, 38)': 'lime',
  'rgb(34, 80, 145)': 'teal',
  '#4A687F': 'blue_cyan',
  '#5166CC': 'brand'
} as const;

export const initialColorList = [
  'rgb(128, 128, 128)',
  'rgb(0, 12, 87)',
  'rgb(253, 171, 61)',
  'rgb(226, 68, 92)',
  'rgb(163, 88, 208)',
  'rgb(0, 202, 114)',
  'rgb(255, 203, 0)',
  'rgb(156, 211, 38)',
  'rgb(34, 80, 145)',
  'rgb(255, 21, 138)'
];
